import React from "react"
import { graphql } from "gatsby"
import RichTextRenderer from "../components/RichTextRenderer"
import Link from "../components/Link"
import {
  Section,
  theme,
  Container,
  Box,
  Flex,
  Heading,
  Button,
} from "@singita/components"
import { Translation } from "react-i18next"
import { useSeo } from "gatsby-singita-theme/src/hooks"
import HelmetWrapper, { getJsonLd } from "gatsby-singita-theme/src/components/Helmet"
import { LocaleLink } from "gatsby-singita-theme/src/components/LocaleLink"
import Main from "gatsby-singita-theme/src/components/Main"
import styled from "@emotion/styled"
import { TEMPLATE_TYPES } from "gatsby-singita-theme/src/common/constants"
import { NavbarContext } from "gatsby-singita-theme/src/context/NavbarContext"
import { getDescription } from "../common/utils"

const ACTIVITY_DATA_MAP = {
  title: ["headline"],
  image: ["images", "gatsbyImageData", "src"],
  altText: ["headline"],
  description: ({ content }) => {
    let rawContent = content?.raw;
    if (rawContent) {
      const parsedContent = JSON.parse(rawContent);
      const hasText = parsedContent.content.some(
        (node) =>
          node.nodeType === "paragraph" &&
          node.content.some(
            (textNode) => textNode.nodeType === "text" && textNode.value.trim() !== ""
          )
      );
      if (!hasText && content?.references?.length) {
        const referenceWithContent = content.references.find((ref) => ref.content?.raw);
        if (referenceWithContent) {
          rawContent = referenceWithContent.content.raw;
        }
      }
    }
    if (!rawContent || typeof rawContent !== "string" || !rawContent.trim().startsWith("{")) {
      return null;
    }
    return getDescription(JSON.parse(rawContent));
  },
}

const ActivityTemplate = (props) => {
  const {
    data: { activity },
    location,
    pageContext,
  } = props
  const { setNavbarStyles } = React.useContext(NavbarContext)
  const isLodgeActivity = pageContext.isLodgeActivity
  const locationTitle = isLodgeActivity ? activity.lodge[0].fullTitle : activity.region.headline
  const activityTitle = `${activity.headline} at ${locationTitle}`
  const seoProps = useSeo(activity, ACTIVITY_DATA_MAP)
  seoProps.title = `${activityTitle} | African Safari | Singita`
  seoProps.location = locationTitle
  const jsonLd = getJsonLd(seoProps, TEMPLATE_TYPES.EXPERIENCE)
  const returnPath = location?.state?.returnPath || (isLodgeActivity ? `${pageContext.basePath}/#lodge-activities-section` : null)

  const StyledHeader = styled(Flex)`
    position: sticky;
    top: 70px;
    left: 0;
    border-bottom: 1px solid ${theme.colors.baseGrayVeryLight};
    z-index: 1000;
    align-items: center;
    background-color: ${theme.colors.bgLighter};
  `

  let links
  if (activity?.links) {
    links = activity.links.map(link => <Link {...link} />)
  }

  React.useEffect(() => {
    setNavbarStyles({ bg: theme.colors.bgLighter })
  }, [])

  return (
    <Main>
      <Container pt={[8]}>
        <HelmetWrapper {...seoProps} schemaJsonLd={jsonLd} />
        <StyledHeader py={[1]}>
          <Flex maxWidth={"1280px"} justifyContent="space-between" width="100%" mx="auto" alignItems="baseline">
            <Box>
              <Heading size="h6">
                {isLodgeActivity ? (activity.lodge[0].meta.activitiesTitle || "What to see and do") : `Activities at ${activity.region.headline}`}
              </Heading>
            </Box>
            <LocaleLink key={activity.contentful_id} data-testid={`activity-${isLodgeActivity ? "lodge" : "region"}`} to={returnPath}>
              <Button variant="text" icon="close" iconPosition="right">
                <Translation>{t => t("Close")}</Translation>
              </Button>
            </LocaleLink>
          </Flex>
        </StyledHeader>
        <Section headline={activityTitle} direction="row" textContainerSx={{ pr: [0, 6], top: "108px" }} renderCta={links} headlineSize="h1">
          <RichTextRenderer content={activity.content} color={theme.colors.textDark} />
        </Section>
      </Container>
    </Main>
  )
}

export default ActivityTemplate

export const ACTIVITY_QUERY = graphql`
  query activityQuery($activityContentfulId: String!) {
    activity: contentfulActivity(contentful_id: { eq: $activityContentfulId }) {
      headline
      id
      tag
      contentful_id
      content {
        raw
        references {
          __typename
          ...RichTextReferences
          ... on ContentfulContent {
            ...Content
          }
        }
      }
      images {
        gatsbyImageData(layout: CONSTRAINED placeholder: NONE quality: 90 width: 400 height: 260)
      }
      links {
        __typename
        ...LinkFields
      }
      structuredData {
        headline
        description { description }
        image { gatsbyImageData(layout: CONSTRAINED placeholder: NONE quality: 90 width: 800) }
        altText
      }
      lodge {
        fullTitle
        slug
        color
        meta { activitiesTitle }
      }
      region {
        contentful_id
        headline
      }
    }
  }
`
